import React from 'react';

export const CardPreview = ({ component, onClick, isSelected, theme, runtime = false }) => {
  const { commonAttrs, specificAttrs } = component;
  
  const style = {
    padding: `${commonAttrs.paddingTop}px ${commonAttrs.paddingRight}px ${commonAttrs.paddingBottom}px ${commonAttrs.paddingLeft}px`,
    margin: `${commonAttrs.marginTop}px ${commonAttrs.marginRight}px ${commonAttrs.marginBottom}px ${commonAttrs.marginLeft}px`,
    backgroundColor: specificAttrs.backgroundColor,
    border: `${specificAttrs.borderWidth}px solid ${specificAttrs.borderColor}`,
    borderRadius: `${specificAttrs.borderRadius}px`,
    boxShadow: specificAttrs.shadow === 'small' ? '0 1px 2px rgba(0,0,0,0.05)' : 
                specificAttrs.shadow === 'medium' ? '0 4px 6px rgba(0,0,0,0.1)' : 
                specificAttrs.shadow === 'large' ? '0 10px 15px rgba(0,0,0,0.15)' : 'none',
    cursor: 'pointer',
    outline: isSelected ? '2px solid #3B82F6' : 'none',
    position: 'relative',
    overflow: 'hidden'
  };

  // Execute action handler for nested components
  const handleNestedAction = (nested, event) => {
    event.stopPropagation(); // Prevent card selection when clicking nested elements
    
    if (!runtime || !nested.actions || nested.actions.length === 0) {
      return;
    }

    // Execute all actions attached to this nested component
    nested.actions.forEach(action => {
      if (action.triggerEvent === 'onClick') {
        console.log('Executing action:', action);
        // In production, this would use the action executor
        // For now, just log it
        if (action.actionJSON?.actionType === 'openPage') {
          console.log('Navigate to:', action.actionJSON.actionConfig?.pageName);
        }
      }
    });
  };

  // Check if nested component should be visible based on validations
  const shouldShowNested = (nested) => {
    if (!nested.validations || nested.validations.length === 0) {
      return true;
    }

    // In production, this would evaluate validations properly
    // For now, show all components in builder mode
    if (!runtime) {
      return true;
    }

    // Simple validation check (can be enhanced)
    return nested.validations.every(validation => {
      // Placeholder validation logic
      return validation.validationType !== 'NEVER_SHOW';
    });
  };

  // Detect layout hints from nested component styles
  const detectLayout = () => {
    const nested = specificAttrs.nestedComponents || [];
    
    // Check if first component has layout hint
    if (nested.length > 0 && nested[0].style?.layout === 'top') {
      return 'image-top';
    }
    
    // Check if there are many text items (list-like)
    const textCount = nested.filter(n => n.type === 'text').length;
    if (textCount >= 3) {
      return 'list';
    }
    
    // Default flex column
    return 'default';
  };

  const layout = detectLayout();

  return (
    <div style={style} onClick={onClick}>
      {/* Title and Content (if present) */}
      {specificAttrs.title && (
        <h3 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '18px', 
          fontWeight: '600',
          color: '#111827'
        }}>
          {specificAttrs.title}
        </h3>
      )}
      {specificAttrs.content && (
        <p style={{ 
          margin: '0 0 12px 0', 
          fontSize: '14px', 
          color: '#6B7280',
          lineHeight: '1.5'
        }}>
          {specificAttrs.content}
        </p>
      )}
      
      {/* Nested Components with Enhanced Layouts */}
      {specificAttrs.nestedComponents && specificAttrs.nestedComponents.length > 0 && (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '8px', 
          marginTop: specificAttrs.title || specificAttrs.content ? '12px' : '0'
        }}>
          {specificAttrs.nestedComponents.map((nested) => {
            // Check visibility based on validations
            if (!shouldShowNested(nested)) {
              return null;
            }

            // Apply custom styles from nested component
            const customStyle = nested.style || {};
            
            if (nested.type === 'text') {
              return (
                <div 
                  key={nested.id} 
                  style={{ 
                    fontSize: customStyle.fontSize || '14px', 
                    color: customStyle.color || '#374151',
                    fontWeight: customStyle.fontWeight || 'normal',
                    textAlign: customStyle.textAlign || 'left',
                    marginTop: customStyle.marginTop || '0',
                    marginBottom: customStyle.marginBottom || '0',
                    lineHeight: customStyle.lineHeight || '1.5',
                    backgroundColor: customStyle.backgroundColor,
                    padding: customStyle.padding,
                    borderRadius: customStyle.borderRadius,
                    display: customStyle.display,
                    textDecoration: customStyle.textDecoration,
                    ...customStyle
                  }}
                  onClick={(e) => handleNestedAction(nested, e)}
                >
                  {nested.content}
                </div>
              );
            }
            
            if (nested.type === 'button') {
              const hasActions = nested.actions && nested.actions.length > 0;
              return (
                <button
                  key={nested.id}
                  style={{
                    padding: customStyle.padding || '10px 20px',
                    backgroundColor: customStyle.backgroundColor || theme?.colors?.primary || '#3B82F6',
                    color: customStyle.color || '#FFFFFF',
                    border: customStyle.border || 'none',
                    borderRadius: customStyle.borderRadius || '8px',
                    fontSize: customStyle.fontSize || '14px',
                    fontWeight: customStyle.fontWeight || '600',
                    cursor: hasActions ? 'pointer' : 'default',
                    alignSelf: customStyle.alignSelf || 'flex-start',
                    width: customStyle.width || 'auto',
                    marginTop: customStyle.marginTop || '0',
                    transition: 'all 0.2s',
                    ...customStyle
                  }}
                  onClick={(e) => handleNestedAction(nested, e)}
                  onMouseEnter={(e) => {
                    if (hasActions) {
                      e.target.style.opacity = '0.9';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (hasActions) {
                      e.target.style.opacity = '1';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {nested.content || 'Button'}
                </button>
              );
            }
            
            if (nested.type === 'image') {
              const imageStyle = {
                width: customStyle.width || '100%',
                height: customStyle.height || 'auto',
                borderRadius: customStyle.borderRadius || '8px',
                objectFit: customStyle.objectFit || 'cover',
                margin: customStyle.margin || '0',
                marginTop: customStyle.marginTop || '0',
                marginBottom: customStyle.marginBottom || '0',
                ...customStyle
              };

              return (
                <div 
                  key={nested.id}
                  style={{ 
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                  onClick={(e) => handleNestedAction(nested, e)}
                >
                  <img
                    src={nested.src || 'https://via.placeholder.com/400x300'}
                    alt={nested.content || 'Image'}
                    style={imageStyle}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                    }}
                  />
                </div>
              );
            }
            
            return null;
          })}
        </div>
      )}

      {/* Empty State */}
      {(!specificAttrs.nestedComponents || specificAttrs.nestedComponents.length === 0) && 
       !specificAttrs.title && !specificAttrs.content && (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: '#9CA3AF',
          fontSize: '14px'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📝</div>
          <div>Empty Card</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            Add title, content, or nested components
          </div>
        </div>
      )}
    </div>
  );
};
